import { useCallback, useEffect, useMemo } from 'react';

import { usePathname } from 'expo-router';
import { useStoreContext } from 'features/store';

import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import { routes } from 'utils/routing';

import { shouldSkipStoreDialogLogic } from '../utils/store-dialog-conditions';

import { useDialogActions } from './use-dialog-actions';
import { useIsStoreUnavailable } from './use-is-store-unavailable';
import { useOrderErrorLogging } from './use-order-error-logging';
import { useRestaurantSearch } from './use-restaurant-search';

const EXCLUDED_ROUTES = [routes.storeLocator, routes.deliveryAddress, routes.store];

export const useStoreOrderUnavailableDialog = () => {
  const { addAppStateChangeListeners, removeAppStateChangeListeners } = useAppStateChangeListener();
  const { noStoreSelected, isSelectingStore } = useStoreContext();
  const pathname = usePathname();
  const isCurrentPathExcluded = useMemo(
    () => EXCLUDED_ROUTES.some(route => pathname.startsWith(route)),
    [pathname]
  );
  const storeUnavailableData = useIsStoreUnavailable();

  const {
    isRestaurantSearchLoading,
    availableRestaurantForDelivery,
    availableRestaurantForPickUp,
    isDeliverySearchComplete,
    isDelivery,
    executeSearch,
    resetSearchState,
  } = useRestaurantSearch();

  const { isDialogOpen, selectNearbyStore, chooseAnotherStore, setIsDialogOpen } = useDialogActions(
    {
      availableRestaurant:
        availableRestaurantForDelivery ?? availableRestaurantForPickUp ?? undefined,
      clearAllSearchState: resetSearchState,
    }
  );

  const shouldSkipLogic = shouldSkipStoreDialogLogic({
    isStoreUnavailable: storeUnavailableData.isStoreUnavailable,
    isCurrentPathExcluded,
    isDialogOpen,
    isSelectingStore,
    isRestaurantSearchLoading,
    noStoreSelected,
  });

  useOrderErrorLogging(isDialogOpen, storeUnavailableData);

  const handleDialogClose = useCallback(() => {
    setIsDialogOpen(false);
    resetSearchState();
  }, [resetSearchState, setIsDialogOpen]);

  // Initialize restaurant search
  useEffect(() => {
    if (shouldSkipLogic) {
      return;
    }
    executeSearch();
  }, [shouldSkipLogic, executeSearch]);

  // Handle dialog state
  useEffect(() => {
    if (shouldSkipLogic) {
      return;
    }

    // Handle delivery order results
    if (isDelivery && isDeliverySearchComplete) {
      if (availableRestaurantForDelivery?._id) {
        // Found available restaurant for delivery - auto-select it
        selectNearbyStore();
        return;
      }
      // No available restaurant for delivery - show dialog
      setIsDialogOpen(true);
      return;
    }

    // Handle pickup orders - always show dialog
    if (!isDelivery) {
      setIsDialogOpen(true);
    }
  }, [
    shouldSkipLogic,
    isDelivery,
    isDeliverySearchComplete,
    availableRestaurantForDelivery,
    selectNearbyStore,
    setIsDialogOpen,
  ]);

  // Close dialog if user navigates to excluded routes
  useEffect(() => {
    if (isDialogOpen && isCurrentPathExcluded) {
      handleDialogClose();
    }
  }, [isDialogOpen, isCurrentPathExcluded, handleDialogClose]);

  useEffect(() => {
    addAppStateChangeListeners(handleDialogClose);
    return () => {
      removeAppStateChangeListeners();
    };
  }, [addAppStateChangeListeners, removeAppStateChangeListeners, handleDialogClose]);

  return {
    isStoreUnavailable: storeUnavailableData,
    isDialogOpen,
    isDelivery,
    selectNearbyStore,
    chooseAnotherStore,
    setIsDialogOpen,
    isRestaurantSearchLoading,
    availableRestaurantForDelivery,
    availableRestaurantForPickUp,
  };
};
